import "../../../components/common/Tables/DataTable.css"
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tooltip } from "@mui/material";
import RootState from "../../../redux/states/root.state";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { DataTableFoot, SortButton } from "../../../components/common/Tables/DataTableParts";

type Order = "asc" | "desc";

/**
 * Progress and Status share one column — the bar and the device's own wording describe
 * the same thing. `sorts` holds every column the cell can be ordered by, so merging the
 * display doesn't cost the separate sort handles.
 */
const headers = [
  { id: "spa_id", label: "GUID", sorts: [{ id: "spa_id", label: "GUID" }] },
  { id: "stage", label: "Stage", sorts: [{ id: "stage", label: "Stage" }] },
  { id: "progress", label: "Progress", sorts: [{ id: "percent", label: "Progress" }, { id: "status", label: "Status" }] },
  { id: "error", label: "Error", sorts: [] },
  { id: "created_at", label: "Started", sorts: [{ id: "created_at", label: "Started" }] },
  { id: "updated_at", label: "Last Update", sorts: [{ id: "updated_at", label: "Last Update" }] },
];

/** A failed tub keeps the stage it died in, so failure has to be read off status/error. */
const hasFailed = (row: any): boolean =>
  row?.status === "failed" || row?.stage === "failed" || !!row?.error;

// Tone shared by the stage pill and the progress bar
const tone = (row: any): "bad" | "good" | "caution" | "info" | "neutral" => {
  if (hasFailed(row)) return "bad";
  switch (row?.stage) {
    case "complete": return "good";
    case "installing": return "caution";
    case "downloading": return "info";
    default: return "neutral";
  }
};

const formatDate = (value: string | null) =>
  value ? <span className="dt-date">{new Date(value).toLocaleString()}</span> : <span className="dt-na">—</span>;

interface MigrationProgressTableProps {
  // Page controls (filters toggle, auto-refresh) shown in the table's toolbar
  toolbar?: React.ReactNode;
}

const MigrationProgressTable: React.FC<MigrationProgressTableProps> = ({ toolbar }) => {
  const navigate = useNavigate();
  const { data: rows, loading, meta } = useSelector((state: RootState) => state.spaUpdateProgress);
  const [searchParams, setSearchParams] = useSearchParams();
  const { canAccess: isStaff } = usePermissionCheck(["view_spa_details"]);

  // Sort lives in the URL so it survives reloads and every auto-refresh tick.
  const orderBy = searchParams.get("sort_by") ?? "updated_at";
  const order: Order = searchParams.get("sort_direction") === "asc" ? "asc" : "desc";

  const handleRequestSort = (header_id: string) => {
    const isAsc = orderBy === header_id && order === "asc";
    const next = new URLSearchParams(searchParams);
    next.set("sort_by", header_id);
    next.set("sort_direction", isAsc ? "desc" : "asc");
    next.delete("page"); // a re-sort invalidates the current page
    setSearchParams(next);
  };

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const totalPages = meta?.total_pages ?? 1;
  const total = meta?.total ?? 0;

  const goToPage = (value: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(value));
    setSearchParams(next);
  };

  const renderCell = (id: string, row: any) => {
    switch (id) {
      case "spa_id":
        return (
          <span className="dt-stack">
            {/* A migrating tub may not exist in the new DB yet — only link when it does. */}
            {isStaff && row?.spa_serial ? (
              <a
                href={`/spa/${row.spa_id}`}
                className="dt-guid"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                  e.preventDefault();
                  navigate(`/spa/${row.spa_id}`);
                }}
              >
                {row.spa_id}
              </a>
            ) : (
              <span className="dt-mono">{row?.spa_id}</span>
            )}
            {row?.spa_serial && <span className="dt-sub">{row.spa_serial}</span>}
          </span>
        );

      case "stage":
        return row?.stage
          ? <span className={`dt-pill ${tone(row)}`}>{row.stage}</span>
          : <span className="dt-na">—</span>;

      case "progress": {
        const percent = Math.min(100, Math.max(0, Number(row?.percent ?? 0)));
        return (
          <span className="mp-progress">
            <span className="mp-bar-row">
              <span className={`mp-bar ${tone(row)}`} role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
                <span className="mp-bar-fill" style={{ width: `${percent}%` }}/>
              </span>
              <span className="mp-percent">{percent.toFixed(2)}%</span>
            </span>
            {/* The device's own wording for what it is doing, beneath the bar it
                describes. Free-form text, so it may be a token or a sentence. */}
            {row?.status && <span className="dt-sub mp-status" title={row.status}>{row.status}</span>}
          </span>
        );
      }

      case "error":
        return row?.error ? (
          <Tooltip title={row.error} arrow>
            <span className="dt-body mp-error">{row.error}</span>
          </Tooltip>
        ) : <span className="dt-na">—</span>;

      case "created_at":
        return formatDate(row?.created_at);

      case "updated_at":
        return formatDate(row?.updated_at);

      default:
        return null;
    }
  };

  return (
    <div className="dt">
      {toolbar && <div className="dt-toolbar">
        <div className="dt-toolbar-start">{toolbar}</div>
      </div>}

      <div className="dt-scroll">
        {loading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading migration progress"/></div>
        ) : rows.length > 0 ? (
          <table className="dt-table mp-table">
            <thead>
              <tr>
                {headers.map((header) => {
                  const activeSort = header.sorts.find((s) => s.id === orderBy);
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      aria-sort={activeSort ? (order === "asc" ? "ascending" : "descending") : undefined}
                    >
                      {header.sorts.length === 0 ? header.label : (
                        <span className="mp-sorts">
                          {header.sorts.map((sort, i) => (
                            <React.Fragment key={sort.id}>
                              {i > 0 && <span className="mp-sorts-sep" aria-hidden="true">/</span>}
                              <SortButton label={sort.label} active={orderBy === sort.id} order={order} onClick={() => handleRequestSort(sort.id)}/>
                            </React.Fragment>
                          ))}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {rows.map((row: any, i: number) => (
                <tr key={row?.id ?? i}>
                  {headers.map((header) => (
                    <td key={header.id}>{renderCell(header.id, row)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state">
            <div className="dt-empty-title">No Migration Progress Found</div>
          </div>
        )}
      </div>

      <DataTableFoot
        shown={rows?.length ?? 0}
        total={total}
        noun={total === 1 ? "Migration" : "Migrations"}
        page={page}
        totalPages={totalPages}
        loading={loading}
        onPage={goToPage}
      />
    </div>
  );
};

export default MigrationProgressTable;
